/**
 * El Inicio (`/home`) lo decide el PLAN: una tienda ve su tablero; un dropshipper, su Inicio.
 *
 * ── POR QUÉ EXISTE (21-sep-2026) ─────────────────────────────────────────────────────────────
 * El Inicio del Drop vivía en `/dropshipping/smartydrop/empezar`, una ruta DEBAJO del catálogo de
 * Smarty Drop. Compartían prefijo, así que la barra lateral marcaba «Drop → Smarty Drop» estando en
 * el Inicio, y «Inicio» nunca se marcaba: dos páginas que se pisaban. El founder: «al ir a Smarty
 * Drop se buguea con la página de inicio; deben ser páginas independientes, y por defecto debe
 * cargar el Inicio».
 *
 * ⭐ AHORA EL INICIO ES `/home` PARA TODOS; lo que cambia es qué se dibuja. La regla es del plan,
 *    como en `RutaSoloParaTiendas`, y no se finge en desarrollo.
 */
import { Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, useFeatureFlags } from '@smarty/shared';
import { Page, Box, InlineStack, Spinner } from '@/lib/polaris';
import { PageLoader } from '@/routes/PageLoader';
import * as Lazy from '@/routes/lazyPages';
import { ROUTES, storePath } from '@/config/routes.config';
import { useDatosCacheados } from '@/hooks/useDatosCacheados';
import { CLAVES_DROP } from '@/lib/dropshipping/clavesDeCache';
import { misPapeles } from '@/lib/dropshipping/actoresDelDrop';
import { A_DONDE_ENTRA_EL_ALMACEN } from '@/lib/dropshipping/rutasDelRolAlmacen';

export function InicioSegunElPlan() {
  const { planType, loading, isInternal } = useFeatureFlags();
  const { tenantId, storeSlug } = useAuth();
  /*
   * ⭐ EL COURIER QUE SÓLO REPARTE ENTRA DIRECTO A LO QUE TIENE QUE RECOGER (20261021004300). El founder, 20-oct: «que
   *    de frente vaya a entregas». Misma regla que la barra lateral (`esCourierExterno`), con la misma consulta cacheada.
   */
  // 🪤 No se espera a los papeles para pintar el Inicio: la consulta es la misma que ya hace la barra (cacheada), y
  //    frenar el Inicio de TODAS las cuentas por el courier subiría el tiempo de carga que mide el RUM.
  const { datos: papeles } = useDatosCacheados<string[]>({
    clave: [CLAVES_DROP.misPapeles, tenantId],
    activo: !!tenantId && !isInternal,
    frescura: 5 * 60_000,
    traer: () => misPapeles(tenantId!),
  });
  const soloCourier = !isInternal && !!papeles?.includes('courier') && !papeles.includes('proveedor') && !papeles.includes('almacen');
  if (soloCourier) return <Navigate to={storeSlug ? storePath(storeSlug, ROUTES.ENTREGAS_POR_RECOGER) : ROUTES.ENTREGAS_POR_RECOGER} replace />;
  // ⭐ Todo almacén entra por sus Guías de despacho (founder, 23-oct). Ver `A_DONDE_ENTRA_EL_ALMACEN`.
  // 🪤 Recién cuando se sabe que NO es un courier que sólo reparte (los papeles ya llegaron): si se saltara antes, al
  //    courier lo mandaría a las guías en vez de a lo que tiene que recoger.
  if (!isInternal && !loading && planType === 'almacen' && papeles) {
    return <Navigate to={storeSlug ? storePath(storeSlug, A_DONDE_ENTRA_EL_ALMACEN) : A_DONDE_ENTRA_EL_ALMACEN} replace />;
  }
  if (loading) {
    return (
      <Page>
        <Box padding="800"><InlineStack align="center"><Spinner size="large" /></InlineStack></Box>
      </Page>
    );
  }
  return (
    <Suspense fallback={<PageLoader variant="content" />}>
      {planType === 'drop' ? <Lazy.BienvenidaDropPage /> : <Lazy.DashboardPage />}
    </Suspense>
  );
}
